import { useConnectWallet, useFetchUserWallet } from '@/features/stake-sol/api'
import { UnlinkWalletButton } from '@/features/stake-sol/components/UnlinkWalletButton'
import { Button } from '@/ui-kit'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useConnection, useWallet } from '@solana/wallet-adapter-react'
import { useWalletModal } from '@solana/wallet-adapter-react-ui'
import { PublicKey, Transaction, TransactionInstruction } from '@solana/web3.js'
import bs58 from 'bs58'
import clsx from 'clsx'
import { useCallback, useEffect, useRef } from 'react'
import { toast } from 'sonner'

const MEMO_PROGRAM_ID = new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr')

const uint8ArrayToBase58 = (uint8Array: Uint8Array): string => {
  return bs58.encode(uint8Array)
}

export const LinkWalletButton = ({ className }: { className?: string }) => {
  const { data: userWallet } = useFetchUserWallet()
  const { mutateAsync: connectWallet, isPending: isConnectLoading } = useConnectWallet()
  const { connection } = useConnection()
  const { publicKey, connected, signTransaction } = useWallet()
  const { setVisible } = useWalletModal()
  const pendingLinkRef = useRef(false)

  const performLink = useCallback(async () => {
    if (!publicKey || !signTransaction) return
    try {
      const timestamp = Date.now().toString().slice(0, 10)
      const address = publicKey.toString()
      const message = `Welcome to DataHive AI! To securely link your wallet, please sign this message\n${timestamp}`

      const memoIx = new TransactionInstruction({
        keys: [],
        programId: MEMO_PROGRAM_ID,
        data: Buffer.from(message, 'utf8')
      })
      const transaction = new Transaction().add(memoIx)
      const { blockhash } = await connection.getLatestBlockhash()
      transaction.recentBlockhash = blockhash
      transaction.feePayer = publicKey

      const signedTx = await signTransaction(transaction)
      const signatureBase58 = uint8ArrayToBase58(new Uint8Array(signedTx.serialize()))

      try {
        await connectWallet({
          walletAddress: address,
          signature: signatureBase58,
          message
        })
      } catch {
        // connectWallet errors are surfaced via toast in the outer catch
      }
    } catch (err) {
      console.error('Wallet connection error:', err)
      toast.error(t`Failed to connect wallet. Please try again.`)
    } finally {
      pendingLinkRef.current = false
    }
  }, [publicKey, signTransaction, connection, connectWallet])

  useEffect(() => {
    if (connected && publicKey && pendingLinkRef.current) {
      void performLink()
    }
  }, [connected, publicKey, performLink])

  const handleConnect = () => {
    if (!connected || !publicKey) {
      pendingLinkRef.current = true
      setVisible(true)
      return
    }
    void performLink()
  }

  return !userWallet?.wallet ? (
    <Button
      variant="bordered"
      className={clsx('gap-2', className)}
      size="small"
      onClick={handleConnect}
      isLoading={isConnectLoading}
      isDisabled={isConnectLoading}
    >
      <Trans>Link Wallet</Trans>
    </Button>
  ) : (
    <div className="flex w-full items-end justify-between">
      <div className="flex grow flex-col gap-2">
        <span className="text-body-3 text-gray-90">
          <Trans>Linked wallet</Trans>
        </span>
        <span className="text-gray-70 font-semibold">
          {userWallet.wallet.walletAddress.slice(0, 6)}...${userWallet.wallet.walletAddress.slice(-5)}
        </span>
      </div>
      <UnlinkWalletButton className={className} isDisabled={isConnectLoading} />
    </div>
  )
}
